// =====================================================================
// packages/ui/cliente.js — Cliente Supabase + contexto do tenant no browser.
//
// LEMBRE: o subdomínio aqui é COSMÉTICO. Ele escolhe logo, cor e nome na
// tela de login. Nada mais. Quem decide o que o usuário enxerga é a RLS
// do Postgres, que olha o JWT — não o endereço da barra do navegador.
// Trocar o subdomínio na mão não dá acesso a dado nenhum.
// =====================================================================

import { createClient } from '@supabase/supabase-js';

// Injetados no build (esbuild --define) — são chaves públicas por natureza.
const SB_URL      = process.env.SB_URL;
const SB_ANON_KEY = process.env.SB_ANON_KEY;
const ROOT_DOMAIN = process.env.ROOT_DOMAIN || 'smartgestaoimobiliaria.com.br';

export const sb = createClient(SB_URL, SB_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
});

/** 'fertat-portal.smart…' → { slug: 'fertat', portal: true } */
export function slugDoHost(host = location.hostname) {
  const h = host.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') {
    return { slug: new URLSearchParams(location.search).get('tenant') || 'demo', portal: /portal/.test(location.port) };
  }
  if (!h.endsWith('.' + ROOT_DOMAIN)) return { slug: null, portal: false };
  const sub = h.slice(0, -(ROOT_DOMAIN.length + 1));
  if (!sub || sub.includes('.')) return { slug: null, portal: false };
  return sub.endsWith('-portal')
    ? { slug: sub.slice(0, -'-portal'.length), portal: true }
    : { slug: sub, portal: false };
}

/** Branding do tenant para a tela de login (dados públicos apenas). */
export async function carregarBranding(slug) {
  if (!slug) return null;
  const { data, error } = await sb
    .from('tenants')
    .select('id,slug,nome,logo_url,cor_primaria,cor_secundaria')
    .eq('slug', slug)
    .maybeSingle();
  if (error) return null;
  return data;
}

/** Aplica as cores do cliente nas CSS custom properties. */
export function aplicarTema(t) {
  if (!t) return;
  const r = document.documentElement.style;
  r.setProperty('--cor-primaria',   t.cor_primaria   || '#0F766E');
  r.setProperty('--cor-secundaria', t.cor_secundaria || '#134E4A');
  document.title = `${t.nome} — Smart Gestão Imobiliária`;
}

/** Perfil do usuário logado (tenant_id, papel). */
export async function meuPerfil() {
  const { data: { user } } = await sb.auth.getUser();
  if (!user) return null;
  const { data } = await sb
    .from('perfis')
    .select('id,tenant_id,nome,email,papel,foto_url,aprovado')
    .eq('id', user.id)
    .maybeSingle();
  return data;
}

/** Chama uma Netlify Function já com o Bearer do usuário. */
export async function api(fn, body, method = 'POST') {
  const { data: { session } } = await sb.auth.getSession();
  if (!session) throw new Error('Sessão expirada');
  const r = await fetch(`/.netlify/functions/${fn}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`
    },
    body: method === 'GET' ? undefined : JSON.stringify(body || {})
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
}
